import { useState } from 'react';

import { useProcgen } from '../../state/procgen.js';
import { FieldEditor } from '../scene/Inspector.js';
import { NumberField, SelectField, TextField, Toggle } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import {
  descriptorToFieldDef,
  diagnosticsAt,
  formatJsonPath,
  freshName,
  groupedParams,
  isValidName,
  localSetNames,
  newDescriptor,
  normalizeProp,
  propValue,
  PROP_TYPES,
  renameKey,
  withPropValue,
  type CategoryRow,
  type ProcgenDiagnostic,
  type ProcgenDoc,
  type PropDecl,
  type PropInput,
  type PropType,
} from './procgen-doc.js';
import { TableGrid } from './TableGrid.js';

/**
 * Params of the open generator (section 3.4): each descriptor is edited
 * with the Inspector's FieldEditor through its FieldDef mapping; tables get
 * a typed grid. The ⋯ button opens the descriptor itself (name, type, range,
 * unit, group, ...).
 */

export function ParamsForm({ doc, file, diagnostics }: { doc: ProcgenDoc; file: string; diagnostics: readonly ProcgenDiagnostic[] }) {
  const edit = useProcgen((s) => s.edit);
  const [type, setType] = useState<PropType>('number');
  const groups = groupedParams(doc);

  return (
    <div className="pcg-params">
      {groups.length === 0 && <div className="muted pcg-empty-note">No params yet. Params are the generator's typed settings; other generators read them as alias.param.</div>}
      {groups.map((g) => (
        <div key={g.group || '_'} className="pcg-param-group">
          {g.group && <div className="pcg-subhead">{g.group}</div>}
          {g.names.map((name) => (
            <ParamRow key={name} name={name} prop={doc.params?.[name] ?? 'number'} doc={doc} file={file} diagnostics={diagnostics} />
          ))}
        </div>
      ))}
      <div className="pcg-add-row">
        <SelectField value={type} options={PROP_TYPES.filter((t) => t !== 'weights')} onChange={(v) => setType(v as PropType)} />
        <button
          type="button"
          className="pcg-btn"
          onClick={() =>
            edit((d) => {
              const name = freshName(d, type === 'table' ? 'table' : 'param');
              return { ...d, params: { ...(d.params ?? {}), [name]: newDescriptor(type) } };
            })
          }
        >
          <Icon name="plus" size={11} /> Add param
        </button>
      </div>
    </div>
  );
}

function ParamRow({ name, prop, doc, file, diagnostics }: { name: string; prop: PropInput; doc: ProcgenDoc; file: string; diagnostics: readonly ProcgenDiagnostic[] }) {
  const setAt = useProcgen((s) => s.setAt);
  const [open, setOpen] = useState(false);
  const p = normalizeProp(prop);
  const path = formatJsonPath(['params', name]);
  const def = descriptorToFieldDef(name, p);
  const diags = diagnosticsAt(diagnostics, file, path, false);
  const set = (next: PropDecl | undefined) => setAt(path, next);

  return (
    <div className={`pcg-param${diags.some((d) => d.severity === 'error') ? ' has-error' : ''}`} data-pcg-path={path}>
      <div className="prop-row">
        <span className="prop-label" title={def.hint ?? name}>
          {def.label}
        </span>
        <div className="prop-control">
          {p.type === 'table' ? (
            <span className="muted">{(p.rows ?? []).length} rows · {Object.keys(p.columns ?? {}).length} columns</span>
          ) : p.type === 'weights' ? (
            <span className="muted">computed only</span>
          ) : (
            <FieldEditor def={def} value={propValue(p)} onChange={(v) => set(withPropValue(p, v))} />
          )}
          <button type="button" className={`ibtn${open ? ' is-active' : ''}`} title="Edit descriptor" aria-label={`Edit descriptor of ${name}`} onClick={() => setOpen((o) => !o)}>
            <Icon name="more" size={13} />
          </button>
        </div>
      </div>
      {p.type === 'table' && (
        <TableGrid
          path={`${path}.rows`}
          doc={doc}
          columns={p.columns ?? {}}
          rows={p.rows ?? []}
          onRows={(rows: CategoryRow[]) => set({ ...p, rows })}
          onColumns={(columns) => set({ ...p, columns })}
          setNames={(ref) => localSetNames(doc, ref)}
        />
      )}
      {open && <DescriptorEditor name={name} prop={p} doc={doc} onChange={set} />}
      {diags.map((d, i) => (
        <div key={i} className={`pcg-expr-msg is-${d.severity}`}>
          <span className="pcg-code">{d.code}</span> {d.message}
        </div>
      ))}
    </div>
  );
}

/** Edits a descriptor's own keys (section 3.3); `onChange(undefined)` deletes the param. */
export function DescriptorEditor({
  name,
  prop,
  doc,
  onChange,
  container = 'params',
}: {
  name: string;
  prop: PropDecl;
  doc: ProcgenDoc;
  onChange: (next: PropDecl | undefined) => void;
  /** Where the name lives (renames go through the store). */
  container?: 'params';
}) {
  const edit = useProcgen((s) => s.edit);
  const [rename, setRename] = useState(name);
  const patch = (key: string, value: unknown) => {
    const next: PropDecl = { ...prop };
    if (value === undefined || value === '') delete next[key];
    else next[key] = value;
    onChange(next);
  };
  const range = prop.range;

  return (
    <div className="pcg-descriptor">
      <div className="prop-row">
        <span className="prop-label">Name</span>
        <div className="prop-control">
          <input
            className={`textfield mono${isValidName(rename) ? '' : ' is-invalid'}`}
            value={rename}
            onChange={(e) => setRename(e.target.value)}
            onBlur={() => {
              if (rename !== name && isValidName(rename) && container === 'params')
                edit((d) => ({ ...d, params: renameKey(d.params ?? {}, name, rename) }));
              else setRename(name);
            }}
            aria-label="param name"
          />
        </div>
      </div>
      <div className="prop-row">
        <span className="prop-label">Type</span>
        <div className="prop-control">
          <SelectField
            value={prop.type}
            options={PROP_TYPES}
            onChange={(t) => {
              const fresh = newDescriptor(t as PropType);
              const keep = Object.fromEntries(['description', 'group', 'unit', 'hidden'].filter((k) => prop[k] !== undefined).map((k) => [k, prop[k]]));
              onChange({ ...fresh, ...keep } as PropDecl);
            }}
          />
        </div>
      </div>
      {(prop.type === 'number' || prop.type === 'range') && (
        <>
          <div className="prop-row">
            <span className="prop-label" title="Declared domain: sliders, validation (PG007) and bounds (section 5.10)">
              Range
            </span>
            <div className="prop-control">
              <NumberField label="min" value={range?.[0]} onChange={(v) => patch('range', [v, range?.[1] ?? Math.max(v, 1)])} />
              <NumberField label="max" value={range?.[1]} onChange={(v) => patch('range', [range?.[0] ?? Math.min(v, 0), v])} />
              {range && (
                <button type="button" className="ibtn" aria-label="Clear range" onClick={() => patch('range', undefined)}>
                  <Icon name="x" size={11} />
                </button>
              )}
            </div>
          </div>
          <div className="prop-row">
            <span className="prop-label">Step</span>
            <div className="prop-control">
              <NumberField value={prop.step} min={0} onChange={(v) => patch('step', v > 0 ? v : undefined)} />
              <Toggle label="Integer" checked={prop.integer === true} onChange={(on) => patch('integer', on || undefined)} />
            </div>
          </div>
        </>
      )}
      {prop.type === 'label' && (
        <div className="prop-row">
          <span className="prop-label" title="A tiny inline set (comma separated)">
            Options
          </span>
          <div className="prop-control">
            <TextField
              value={(prop.options ?? []).join(', ')}
              onChange={(v) => patch('options', v.split(',').map((s) => s.trim()).filter(Boolean))}
            />
          </div>
        </div>
      )}
      {(prop.type === 'label' || prop.type === 'labels') && (
        <div className="prop-row">
          <span className="prop-label" title="Set ref: another output's set (biomes.zone), an own output or a table param">
            Of
          </span>
          <div className="prop-control">
            <TextField value={prop.of} placeholder="biomes.zone" onChange={(v) => patch('of', v.trim() || undefined)} list="pcg-set-refs" />
            <datalist id="pcg-set-refs">
              {Object.keys(doc.params ?? {})
                .filter((k) => normalizeProp(doc.params![k]!).type === 'table')
                .concat(Object.keys(doc.outputs))
                .map((k) => (
                  <option key={k} value={k} />
                ))}
            </datalist>
          </div>
        </div>
      )}
      {prop.type === 'asset' && (
        <div className="prop-row">
          <span className="prop-label">Suffix</span>
          <div className="prop-control">
            <TextField value={Array.isArray(prop.suffix) ? prop.suffix.join(', ') : prop.suffix} placeholder=".prefab.json" onChange={(v) => {
              const parts = v.split(',').map((s) => s.trim()).filter(Boolean);
              patch('suffix', parts.length > 1 ? parts : parts[0]);
            }} />
          </div>
        </div>
      )}
      <div className="prop-row">
        <span className="prop-label">Unit</span>
        <div className="prop-control">
          <TextField value={prop.unit} placeholder="m, deg, …" onChange={(v) => patch('unit', v)} />
        </div>
      </div>
      <div className="prop-row">
        <span className="prop-label">Group</span>
        <div className="prop-control">
          <TextField value={prop.group} onChange={(v) => patch('group', v)} />
          <Toggle label="Hidden" checked={prop.hidden === true} onChange={(on) => patch('hidden', on || undefined)} />
        </div>
      </div>
      <div className="prop-row">
        <span className="prop-label">Description</span>
        <div className="prop-control">
          <TextField value={prop.description} onChange={(v) => patch('description', v)} />
        </div>
      </div>
      <div className="pcg-descriptor-bar">
        <button type="button" className="pcg-btn is-danger" onClick={() => onChange(undefined)}>
          <Icon name="trash" size={11} /> Delete {name}
        </button>
      </div>
    </div>
  );
}
